import { useState } from "react";
import { useNavigate } from "react-router-dom";
export interface VIPProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  sellingPrice: number;
  raised: number;
  target: number;
  stockQuantity: number;
  returnRate: number;
  imageUrl?: string;
  tag: string;
  status: string;
}
import AuthModal from "../../subscribe-trial/components/AuthModal";
import { useAuth } from "../../../hooks/useAuth";

interface VIPProductCardProps {
  product: VIPProduct;
  isVIP: boolean;
  onOrder: (productId: string) => void;
}

function VIPProductCard({ product, isVIP, onOrder }: VIPProductCardProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [imgError, setImgError] = useState(false);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const profit = product.sellingPrice - product.price;
  const remaining = Math.max(0, product.stockQuantity - product.raised);

  const handleOrder = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!isVIP) {
      return;
    }
    navigate(`/product/${product.id}`);
  };

  return (
    <>
      <div className="group relative bg-stone-800 rounded-2xl border border-stone-700 overflow-hidden hover:border-amber-500/40 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/5">
        {/* Clickable card area */}
        <button
          onClick={() => navigate(`/product/${product.id}`)}
          className="w-full text-left block"
        >
          {/* Image area */}
          <div className="relative w-full h-48 md:h-56 bg-stone-700 flex items-center justify-center overflow-hidden">
            {product.imageUrl && !imgError ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-amber-400 text-xl" />
                </div>
                <span className="font-body text-xs text-stone-500">{product.name}</span>
              </div>
            )}

            {/* VIP tag */}
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 font-body text-[10px] font-semibold border border-amber-500/20">
                <i className="ri-vip-crown-line" />
                {product.tag}
              </span>
            </div>

            {/* Remaining badge */}
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-stone-900/80 backdrop-blur-sm text-stone-300 font-body text-[10px] font-semibold border border-stone-700">
                <i className="ri-fire-line text-rose-400" />
                剩 {remaining} 件
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-5">
            <h3 className="font-display text-base md:text-lg font-semibold text-white mb-1 line-clamp-1">
              {product.name}
            </h3>
            <p className="font-body text-xs text-stone-400 mb-3 line-clamp-2 leading-relaxed">
              {product.description}
            </p>

            {/* VIP info row - blurred for non-VIP */}
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-stone-700">
              <div className="flex-1">
                <p className="font-body text-[10px] text-stone-500 mb-1">認購價格</p>
                {isVIP ? (
                  <p className="font-display text-sm font-bold text-amber-400">
                    NT$ {product.price.toLocaleString()}
                  </p>
                ) : (
                  <p className="font-display text-sm font-bold text-stone-600 select-none blur-[4px]">
                    NT$ ****
                  </p>
                )}
              </div>
              <div className="flex-1">
                <p className="font-body text-[10px] text-stone-500 mb-1">銷售價</p>
                {isVIP ? (
                  <p className="font-display text-sm font-bold text-emerald-400">
                    NT$ {product.sellingPrice.toLocaleString()}
                  </p>
                ) : (
                  <p className="font-display text-sm font-bold text-stone-600 select-none blur-[4px]">
                    NT$ ****
                  </p>
                )}
              </div>
              <div>
                <p className="font-body text-[10px] text-stone-500 mb-1">利潤</p>
                {isVIP ? (
                  <span className="font-body text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                    NT$ {profit.toLocaleString()}
                  </span>
                ) : (
                  <span className="font-body text-[10px] font-semibold text-stone-600 bg-stone-700 px-1.5 py-0.5 rounded select-none blur-[4px]">
                    NT$ ****
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>

        {/* CTA */}
        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOrder();
            }}
            disabled={isLoggedIn && !isVIP}
            className={`w-full font-body text-xs md:text-sm font-semibold py-3 rounded-full transition-colors whitespace-nowrap ${
              isLoggedIn && !isVIP
                ? "bg-stone-700 text-stone-500 cursor-not-allowed"
                : "bg-amber-500 text-stone-900 hover:bg-amber-400"
            }`}
          >
            {!isLoggedIn ? "登入下單" : isVIP ? "立即認購" : "VIP 專屬"}
          </button>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => onOrder(product.id)}
      />
    </>
  );
}

interface VIPProductGridProps {
  products: VIPProduct[];
  isVIP: boolean;
  onOrder: (productId: string) => void;
}

export default function VIPProductGrid({ products, isVIP, onOrder }: VIPProductGridProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
      {products.map((product) => (
        <VIPProductCard key={product.id} product={product} isVIP={isVIP} onOrder={onOrder} />
      ))}
    </div>
  );
}